import { ReactNode, useEffect, useRef, useState } from "react";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { useMobileAppLayoutSafe } from "@/lib/providers/mobile-app-layout-context";
import { cn } from "@/lib/utils";

export interface PageNavigationBarProps {
  title: string;
  subtitle?: string;
  backButton?: ReactNode;
  leadElement?: ReactNode;
  trailingElements?: ReactNode[];
  bottomElement?: ReactNode;
}

const EMPTY_ARRAY: ReactNode[] = [];

function PageNavigationBarDesktop({
  title,
  subtitle,
  backButton,
  leadElement,
  trailingElements = EMPTY_ARRAY,
  bottomElement,
}: PageNavigationBarProps) {
  const navRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const mobileLayout = useMobileAppLayoutSafe();
  const setNavBarHeight = mobileLayout?.setNavBarHeight;

  // Measure and report nav bar height
  useEffect(() => {
    const measureHeight = () => {
      if (navRef.current && setNavBarHeight) {
        const height = navRef.current.offsetHeight;
        setNavBarHeight(height + 30);
      }
    };

    // Measure on mount and when content changes
    measureHeight();

    // Set up ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(measureHeight);
    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [setNavBarHeight]);

  // Scroll detection for this specific navigation bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Sticky Navigation Container */}
      <div
        ref={navRef}
        className={cn(
          "mx-auto max-w-4xl pt-4 px-2 sticky top-4 z-30 transition-all duration-200 ease-in-out rounded-lg",
          isScrolled && ["bg-background/80 backdrop-blur-md", "border border-border", "shadow-lg"]
        )}
      >
        {/* Back Button (above title) */}
        {backButton && <div className="px-3">{backButton}</div>}

        {/* Header with Title and Actions */}
        <div className="flex items-center justify-between gap-4 mb-4 px-3">
          <div className="flex items-center gap-4 flex-1">
            {/* Title and Subtitle */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground">{title}</h1>
              {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
            </div>

            {/* Lead Element */}
            {leadElement && <div className="flex items-center gap-2">{leadElement}</div>}
          </div>

          <div className="flex items-center gap-3">
            {/* Trailing Elements */}
            {trailingElements.map((element, index) => (
              <div key={index}>{element}</div>
            ))}
          </div>
        </div>

        {/* Bottom Element */}
        {bottomElement && <div className="mb-6 px-3">{bottomElement}</div>}
      </div>
    </>
  );
}

function PageNavigationBarMobile({
  title,
  backButton,
  leadElement,
  trailingElements = EMPTY_ARRAY,
  bottomElement,
}: PageNavigationBarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const leadRef = useRef<HTMLDivElement>(null);
  const trailingRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const [showTitle, setShowTitle] = useState(true);

  // Use mobile app layout context for height reporting only
  const mobileLayout = useMobileAppLayoutSafe();
  const setNavBarHeight = mobileLayout?.setNavBarHeight;
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll detection for this specific navigation bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Measure and report nav bar height
  useEffect(() => {
    const measureHeight = () => {
      if (navRef.current && setNavBarHeight) {
        const height = navRef.current.offsetHeight;
        setNavBarHeight(height);
      }
    };

    // Measure on mount and when content changes
    measureHeight();

    // Set up ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(measureHeight);
    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [setNavBarHeight]);

  useEffect(() => {
    const checkTitleVisibility = () => {
      if (!containerRef.current || !leadRef.current || !trailingRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const leadWidth = leadRef.current.offsetWidth;
      const trailingWidth = trailingRef.current.offsetWidth;
      const padding = 24; // 12px on each side (px-3)

      // Estimate title width (approximate based on text length)
      const titleWidth = title.length * 8 + 20; // rough estimation

      // Calculate available space for title
      const availableWidth = containerWidth - leadWidth - trailingWidth - padding * 2;

      // Hide title if there's not enough space (with some buffer)
      const shouldShowTitle = availableWidth > titleWidth + 60; // 60px buffer for earlier hiding
      setShowTitle(shouldShowTitle);
    };

    // Check on mount and when elements change
    checkTitleVisibility();

    // Add resize observer to handle dynamic changes
    const resizeObserver = new ResizeObserver(checkTitleVisibility);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [title]);

  return (
    <div className="md:hidden">
      <div
        ref={navRef}
        className={cn(
          "fixed top-0 left-0 right-0",
          "z-80 transition-all duration-200 ease-in-out",
          isScrolled && [
            "backdrop-blur-xl",
            "border-b border-border/70",
            "shadow-md shadow-black/10",
          ]
        )}
        style={{
          paddingTop: "var(--safe-area-inset-top, 0px)",
        }}
      >
        {/* Top Navigation Bar */}
        <div ref={containerRef} className="relative flex items-center px-3 py-3 min-h-16">
          {/* Left Side - Back Button and Lead Element */}
          <div ref={leadRef} className="flex items-center gap-2">
            {backButton}
            {leadElement}
          </div>

          {/* Centered Title - conditionally rendered */}
          {showTitle && (
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <h1 className="text-lg font-semibold text-foreground whitespace-nowrap">{title}</h1>
            </div>
          )}

          {/* Right Side - Trailing Elements */}
          <div ref={trailingRef} className="flex items-center gap-2 ml-auto">
            {trailingElements.map((element, index) => (
              <div key={index}>{element}</div>
            ))}
          </div>
        </div>

        {/* Bottom Element */}
        {bottomElement && <div className="px-3 pb-3">{bottomElement}</div>}
      </div>
    </div>
  );
}

export function PageNavigationBar(props: PageNavigationBarProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <PageNavigationBarMobile {...props} />;
  }
  return <PageNavigationBarDesktop {...props} />;
}
